/**
 * Dark Theme Configuration
 *
 * Ant Design theme configuration for dark mode
 */

import type { ThemeConfig } from 'antd';
import { theme } from 'antd';
import { colors, borderRadius } from './tokens';

export const darkTheme: ThemeConfig = {
  token: {
    // Brand colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,

    // Background colors
    colorBgBase: colors.dark.bg,
    colorBgContainer: colors.dark.bgSecondary,
    colorBgElevated: colors.dark.bgTertiary,
    colorBgLayout: colors.dark.bgLayout,
    colorBgSpotlight: colors.dark.bgTertiary,

    // Text colors
    colorText: colors.dark.text,
    colorTextSecondary: colors.dark.textSecondary,
    colorTextTertiary: colors.dark.textTertiary,
    colorTextQuaternary: colors.dark.textDisabled,

    // Border colors
    colorBorder: colors.dark.border,
    colorBorderSecondary: colors.dark.borderSecondary,

    // Border radius
    borderRadius: borderRadius.base,
    borderRadiusLG: borderRadius.md,
    borderRadiusSM: borderRadius.sm,

    // Font
    fontSize: 14,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,

    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,

    // Control sizes
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 24,

    // Shadows
    boxShadow: `0 2px 8px ${colors.dark.shadow}`,
    boxShadowSecondary: `0 4px 12px ${colors.dark.shadowMedium}`,
  },

  components: {
    Layout: {
      headerBg: colors.dark.bgSecondary,
      headerColor: colors.dark.text,
      bodyBg: colors.dark.bgLayout,
      siderBg: colors.dark.bg,
    },

    Menu: {
      darkItemBg: colors.dark.bg,
      darkItemColor: colors.dark.textSecondary,
      darkItemHoverBg: colors.dark.hover,
      darkItemSelectedBg: colors.primary,
      darkItemSelectedColor: '#ffffff',
      darkSubMenuItemBg: colors.dark.bg,
      darkPopupBg: colors.dark.bg,
      itemBg: colors.dark.bg,
      subMenuItemBg: colors.dark.bg,
      popupBg: colors.dark.bg,
    },

    Card: {
      colorBgContainer: colors.dark.bgSecondary,
      boxShadow: `0 1px 2px ${colors.dark.shadow}`,
    },

    Table: {
      headerBg: colors.dark.bgTertiary,
      headerColor: colors.dark.text,
      rowHoverBg: colors.dark.hover,
      colorBgContainer: colors.dark.bgSecondary,
    },

    Button: {
      primaryShadow: `0 2px 0 ${colors.dark.shadow}`,
    },

    Drawer: {
      colorBgElevated: colors.dark.bgSecondary,
      colorBgMask: 'rgba(0, 0, 0, 0.65)',
    },

    Modal: {
      contentBg: colors.dark.bgSecondary,
      headerBg: colors.dark.bgSecondary,
    },

    Input: {
      colorBgContainer: colors.dark.bg,
    },

    Select: {
      colorBgContainer: colors.dark.bg,
      colorBgElevated: colors.dark.bgSecondary,
    },
  },

  algorithm: theme.darkAlgorithm, // Use Ant Design's dark algorithm
};
